import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Inject, Injectable, Optional } from '@angular/core';

import { AnalyticsEvent } from '../../Domain/Model/analytics.event';
import { Consent } from '../../Domain/Model/Consent/consent';
import { ConsentDialogManager } from '../../Domain/Port/consent-dialog.manager';
import { ConsentRepository, defaultConsent } from '../../Domain/Port/consent.repository';
import { EventRepository } from '../../Domain/Port/event-repository';
import { FirebaseErrorLogger } from '../../Domain/Port/firebase-error.logger';
import { ANALYTICS_ESSENTIAL_EVENTS } from '../Provider/injection-token/essentaial-events.it';
import { EventRepositoryAnalyticsService } from './event-repository-analytics.service';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
/**
 * A decorator service for analytics event logging that respects user consent settings.
 * This service wraps the standard event repository and adds consent-aware functionality,
 * ensuring events are only logged when consent is given or when they are essential.
 * Non-essential events are queued when consent is not given and can be flushed once consent is obtained.
 */
export class ConsentAwareEventRepositoryServiceDecorator extends EventRepositoryAnalyticsService {
    /**
     * Queue to store events that couldn't be logged due to missing consent
     * Events are stored here until consent is given or the queue reaches maxQueueSize
     */
    private readonly eventsQueue: AnalyticsEvent[] = [];

    /**
     * Current user consent status
     * Initialized with default consent (not given, not prompted)
     */
    private currentConsent: Consent = defaultConsent;

    /**
     * Maximum number of events to store in the queue
     * Limited to 1000 to prevent memory issues
     */
    private readonly maxQueueSize = 1000;

    /**
     * Creates an instance of ConsentAwareEventRepositoryServiceDecorator.
     *
     * @param eventRepository - The underlying event repository for logging analytics events
     * @param consentStorageService - Service for retrieving and storing user consent preferences
     * @param consentPresentationService - Service for presenting consent dialogs to users
     * @param firebaseErrorLogger - Service for logging errors that occur during event logging
     * @param essentialEvents - List of event names that are considered essential and can be logged without consent
     */
    constructor(
        eventRepository: EventRepository,
        private readonly consentStorageService: ConsentRepository,
        private readonly consentPresentationService: ConsentDialogManager,
        private readonly firebaseErrorLogger: FirebaseErrorLogger,
        @Optional()
        @Inject(ANALYTICS_ESSENTIAL_EVENTS)
        private readonly essentialEvents: string[]
    ) {
        super(eventRepository);
        this.consentStorageService
            .getConsent()
            .pipe(untilDestroyed(this))
            .subscribe(async consent => {
                this.currentConsent = consent;
                await eventRepository.setAnalyticsConsent(consent.isGiven);
                if (consent.isGiven) {
                    this.flushQueuedEvents().then();
                }
            });
    }

    /**
     * Logs a custom analytics event, respecting user consent settings.
     * If consent is given or the event is essential, it's logged immediately.
     * Otherwise, the event is queued and the consent dialog may be shown.
     *
     * @param event - The analytics event to log
     * @returns A promise that resolves when the event is logged or queued
     */
    override async logCustomEvent(event: AnalyticsEvent): Promise<void> {
        if (this.shouldLogEvent(event)) {
            await super.logCustomEvent(event);
            return;
        }

        this.queueEvent(event);
        await this.handleConsentRequired();
    }

    /**
     * Determines whether an event should be logged based on consent status and event type.
     * Events are logged if either consent is given or the event is essential.
     *
     * @param event - The analytics event to check
     * @returns True if the event should be logged, false otherwise
     */
    private shouldLogEvent(event: AnalyticsEvent): boolean {
        return this.currentConsent.isGiven || this.isEssentialEvent(event);
    }

    /**
     * Checks if an event is considered essential and can be logged without consent.
     *
     * @param event - The analytics event to check
     * @returns True if the event is essential, false otherwise
     */
    private isEssentialEvent(event: AnalyticsEvent): boolean {
        return this.essentialEvents.includes(event.name);
    }

    /**
     * Adds an event to the queue for later processing when consent is given.
     * If the queue is full (reaches maxQueueSize), the oldest event is removed.
     *
     * @param event - The analytics event to queue
     */
    private queueEvent(event: AnalyticsEvent): void {
        if (this.eventsQueue.length >= this.maxQueueSize) {
            this.eventsQueue.shift();
        }

        this.eventsQueue.push(event);
    }

    /**
     * Processes all queued events when consent is given.
     * Events are logged in the order they were queued.
     * If an error occurs while logging an event, it's logged and processing continues.
     *
     * @returns A promise that resolves when all queued events have been processed
     */
    private async flushQueuedEvents(): Promise<void> {
        while (this.eventsQueue.length > 0) {
            const event = this.eventsQueue.shift();
            if (event) {
                try {
                    await super.logCustomEvent(event);
                } catch (error) {
                    await this.firebaseErrorLogger.logError(event, error);
                    // Continue processing the queue despite the error
                }
            }
        }
    }

    /**
     * Handles the case when consent is required but not yet given.
     * If the user hasn't been prompted for consent yet, shows the consent dialog.
     *
     * @returns A promise that resolves when the consent handling is complete
     */
    private async handleConsentRequired(): Promise<void> {
        if (!this.currentConsent.hasBeenPrompted) {
            await this.consentPresentationService.presentConsent();
        }
    }
}
